import { ErrorResponse, isErrorResponse } from "@types"
import { CoinData, SupportersResp } from "@/types/coins"
import customFetch from "./helper"
import { safeEncode } from "../utils"
import { Address } from "viem"

interface UncollectedFeesResult {
  poolAddress: Address
  fees0: string
  fees1: string
  success: boolean
  error?: any
}

export interface BlogUncollectedFeesResult {
  blogId: string
  totalFees0: string
  totalFees1: string
  totalUncollectedFees: UncollectedFeesResult[]
  success: boolean
  error?: any
}

/**
 * Fetches coin data including price, numTokensToSell, and totalSupply
 * Returns string values of BigNumber instances for precision calculations
 */
export async function getCoinData(
  blogSlug: string,
  postSlug: string
): Promise<CoinData | null> {
  const response = await customFetch<CoinData | ErrorResponse>(
    `/coins/data/${safeEncode(blogSlug)}/${safeEncode(postSlug)}`
  )

  if (isErrorResponse(response)) {
    console.error("Failed to fetch coin data:", response.msg)
    return null
  }

  return response
}

/**
 * Fetches coin supporters list including holdings and holding percentages.
 * @param coinId ID of the coin, found on the post object.
 * @param onlyAvatars Only return supporters that have avatars. Helpful for the avatars facepile on the post.
 */
export async function getCoinSupporters({
  coinId,
  onlyAvatars,
  limit,
  minHoldings,
  maxHoldings,
}: {
  coinId: string
  onlyAvatars?: boolean
  limit?: number
  minHoldings?: BigInt
  maxHoldings?: BigInt
}): Promise<SupportersResp | undefined> {
  try {
    const params = new URLSearchParams()

    if (onlyAvatars) {
      params.append("onlyAvatars", "true")
    }
    if (limit) {
      params.append("limit", limit.toString())
    }
    if (minHoldings) {
      params.append("minHoldings", minHoldings.toString())
    }
    if (maxHoldings) {
      params.append("maxHoldings", maxHoldings.toString())
    }

    const url = `/coins/post/${coinId}${params.toString() ? `?${params.toString()}` : ""}`

    const response = await customFetch<SupportersResp | ErrorResponse>(
      url.toString()
    )

    if (isErrorResponse(response)) {
      console.error("Failed to fetch coin supporters:", response.msg)
      return undefined
    }

    return response
  } catch (ex) {
    console.error("Except trying to fetch coin supporters.", ex)
  }
}

/**
 * Fetches uncollected fees for all coins owned by a blog
 * @param blogId The blog ID to fetch uncollected fees for
 * @returns Promise containing the uncollected fees data or null if failed
 */
export async function getBlogUncollectedFees(
  blogId: string
): Promise<BlogUncollectedFeesResult | null> {
  try {
    const response = await customFetch<
      BlogUncollectedFeesResult | ErrorResponse
    >(`/coins/uncollected-fees/${safeEncode(blogId)}`)

    if (isErrorResponse(response)) {
      console.error("Failed to fetch blog uncollected fees:", response.msg)
      return null
    }

    return response
  } catch (ex) {
    console.error("Exception trying to fetch blog uncollected fees.", ex)
    return null
  }
}
